import React, { useState, useEffect } from "react";
import calendar from "../../assets/images/calendar.png";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import stylesTab from "./Report.module.scss";
import Quarter from "./Quarter.tsx";
import FinanceTable from "./FinanceTable.tsx";
import { Icon } from "../../assets/Icons.tsx";
import BreadCrumb from "../CommonComponents/BreadCrumb.tsx";
import { useDispatch } from "react-redux";
import { investorReportData } from "../../redux/slices/investorSlice.js";
import { motion } from "framer-motion";


const Reports: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [isYearPickerOpen, setIsYearPickerOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("Financial Results");
  const [reportData, setReportData] = useState([]);

  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };

  const renderTabContent = (downloadFile) => {
    const tabComponents = {
      "Financial Results": (item) => (
        <div key={item.tab_name}>
          <Quarter
            financialYear={financialYear}
            financialQuarterData={item?.tab_details?.contents || []}
          />
        </div>
      ),
      "Annual Reports": (item) => (
        <div className="row" key={item.tab_name}>
          <div className="col-12">
            <FinanceTable
              cardData={item?.tab_details?.contents || []}
              SubsidiaryCompanies={false}
              financialYear={financialYear}
            />
            <div className="d-flex align-items-center mt-5">
              <Icon name="pdfIcon" height="40px" width="40px" />
              <p
                className={`para fw-bold mb-0 ${stylesTab.textStyle}`}
              >
                <a href="https://www.aptech-worldwide.com/downloads/TermsOfId/Letter-of-Appointment.pdf">
                  Terms Of ID
                </a>
              </p>
              <span
                className={stylesTab.downloadCircleDiv}
                onClick={() => downloadFile()}
              >
                <Icon
                  name="circleDownArrow"
                  height="40px"
                  width="40px"
                  color="#036995"
                />
              </span>
            </div>
          </div>
        </div>
      ),

      "Shareholding Patterns": (item) => (
        <div key={item.tab_name}>
          <Quarter
            financialYear={financialYear}
            financialQuarterData={item?.tab_details?.contents || []}
          />
        </div>
      ),
      "Subsidiary Companies": (item) => (
        <div key={item.tab_name}>
          <FinanceTable
            cardData={item?.tab_details?.contents || []}
            SubsidiaryCompanies={true}
            financialYear={financialYear}
          />
          <div className="d-flex align-items-center mt-5">
            <Icon name="pdfIcon" height="40px" width="40px" />
            <p className={`para fw-bold mb-0 ${stylesTab.textStyle}`}>
              <a href="https://www.aptech-worldwide.com/downloads/TermsOfId/Letter-of-Appointment.pdf">
                Terms Of ID
              </a>
            </p>
            <Icon
              name="circleDownArrow"
              height="40px"
              width="40px"
              color="#036995"
            />
          </div>
        </div>
      ),
    };

    const activeItem = reportData.find(
      (item) => item.tab_details.name === activeTab
    );
    return activeItem ? (
      tabComponents[activeTab]?.(activeItem)
    ) : (
      <div>No Content</div>
    );
  };

  const calculateFinancialYear = (year) => {
    return `FY ${year}`;
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setFinancialYear(calculateFinancialYear(date));
    setIsYearPickerOpen(false);

    setIsCalendarOpen(false);
  };

  const breadcrumb = [
    { title: "Home", link: "/" },
    { title: "Investors", link: "/investors" },
    { title: "Results and Reports", link: "" },
  ];
  const startYear = 2014;
  const endYear = new Date().getFullYear();
  const years = [];

  for (let year = endYear - 1; year >= startYear; year--) {
    years.push(`${year}-${year + 1}`);
  }
  const [selectedYear, setSelectedYear] = useState(years[0]);
  const [financialYear, setFinancialYear] = useState(selectedYear);
  const [loading, setLoading] = useState(true);

  const handleYearChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedYear(event.target.value);
    setFinancialYear(event.target.value);
  };

  const dispatch = useDispatch();

  useEffect(() => {
    const fetchReportData = async () => {
      setLoading(true);
      try {
        const data = await dispatch(investorReportData(selectedYear)).unwrap();
        // const result = data.investor_reports.filter((item)=> item.tab_details.contents.length > 0)
        // console.log(result,'result')
        setReportData(data.investor_reports);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching investor overview data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchReportData();
  }, [dispatch, selectedYear]);

  const downloadFile = async () => {
    const url =
      "https://www.aptech-worldwide.com/downloads/TermsOfId/Letter-of-Appointment.pdf";

    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error("Failed to fetch the PDF file.");
      }
      const blob = await response.blob();
      const blobURL = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobURL;
      link.download = "Terms_of_id.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobURL);
    } catch (error) {
      console.error("Error downloading the file:", error);
    }
  };

  return (
    <div className={stylesTab.ReportsSection}>
      <BreadCrumb menu={breadcrumb} />

      <motion.div
  initial={{ opacity: 0, y: 100 }}
  whileInView={{ opacity: 1, y: 0 }}
  viewport={{ once: true, amount: 0.5 }} // Triggers animation when 50% of the element is visible
  transition={{ duration: 1, ease: "easeInOut" }}
  >
      <div className="container">
        <div className={stylesTab.bannerText}>
          <div className="row mt-5">
            <div className="col-lg-6 col-sm-12 col-md-12">
              <p className="heading">Results and Reports </p>
              <p className="para">
                See Aptech's Quarterly & Annual Reports and related Press
                Releases. 
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="section">
        <div className="container">
          <div className="row">
            <div className="col-12 d-flex flex-column justify-content-center align-items-center">
              <div
                className={`p-2 d-flex justify-content-center align-items-center ${stylesTab.yearDropdownDiv}`}
              >
                <img
                  src={calendar}
                  alt="calendar"
                  className={stylesTab.calendarIcon}
                />
                <span className="para mb-0">Financial Year |</span>
                <div className={stylesTab.customSelect}>
                  <select
                    value={selectedYear}
                    onChange={handleYearChange}
                    className={`para mb-0 ${stylesTab.textPadding}`}
                  >
                    {years?.map((year) => (
                      <option key={year} value={year}>
                        <span style={{ color: "white" }}>FY {year}</span>
                      </option>
                    ))}
                  </select>
                  <div className={stylesTab.customIcon}>
                    <Icon
                      name="dropDownArrow"
                      width="30px"
                      height="50px"
                      color="white"
                      strokeWidth="1"
                    />
                  </div>
                </div>
              </div>

              {isCalendarOpen && (
                <DatePicker
                  selected={selectedDate}
                  onChange={handleDateChange}
                  inline
                />
              )}

              {isYearPickerOpen && (
                <DatePicker
                  selected={selectedDate}
                  onChange={handleDateChange}
                  showYearPicker
                  dateFormat="yyyy"
                  inline
                />
              )}
            </div>
          </div>
        </div>

        {!loading ? (
          <div className="row g-0">
            <div className="col-12 d-flex flex-column justify-content-center">
              {reportData?.length > 0 && (
                <div className="d-flex justify-content-center">
                  <ul
                    className={`nav nav-pills ${stylesTab.navPills} d-flex overflow-auto`}
                    id="pills-tab"
                    role="tablist"
                    style={{ whiteSpace: "nowrap" }}
                  >
                    {reportData?.map((tab) => (
                      <li
                        key={tab?.tab_details?.name}
                        className="nav-item me-3 flex-shrink-0"
                        role="presentation"
                      >
                        <button
                          className={`${stylesTab.navLink} ${
                            activeTab === tab?.tab_details?.name
                              ? stylesTab.navLinkActive
                              : ""
                          }`}
                          onClick={() => handleTabClick(tab?.tab_details?.name)}
                          type="button"
                          role="tab"
                          aria-controls={`pills-${tab?.tab_details?.name.toLowerCase()}`}
                          aria-selected={activeTab === tab?.tab_details?.name}
                        >
                          {tab?.tab_details?.name}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="container">
                <div className="tab-content w-100" id="pills-tabContent">
                  {renderTabContent(downloadFile)}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <p className="para text-center pt-5">Loading...</p>
        )}
      </div>
      </motion.div>
    </div>
  );
};

export default Reports;
